import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import "./Navbar.css";
import logo from '../images/logo-insiightdraft.png';
import { LogOut, Save, Coins } from 'lucide-react';

// Add this CustomPopup component before Navbar function
const CustomPopup = ({ message, type, onClose, onConfirm, showConfirm }) => {
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
        }} onClick={onClose}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: 'clamp(20px, 5vw, 32px)',
                maxWidth: 'min(400px, 90vw)',
                width: '100%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                animation: 'slideIn 0.3s ease',
                textAlign: 'center'
            }} onClick={(e) => e.stopPropagation()}>
                <div style={{ fontSize: 'clamp(40px, 8vw, 60px)', marginBottom: '16px' }}>
                    {type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️'}
                </div>
                <h4 style={{
                    color: type === 'error' ? '#dc3545' : type === 'warning' ? '#f59e0b' : '#0d53bd',
                    marginBottom: '16px',
                    fontSize: 'clamp(16px, 4vw, 20px)',
                    fontWeight: '600'
                }}>
                    {type === 'error' ? 'Oops!' : type === 'warning' ? 'Warning' : type === 'success' ? 'Success!' : 'Notice'}
                </h4>
                <p style={{ color: '#555', marginBottom: '24px', fontSize: 'clamp(14px, 3vw, 16px)', lineHeight: '1.6' }}>
                    {message}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    {showConfirm ? (
                        <>
                            <button onClick={onConfirm} style={{ background: 'linear-gradient(135deg, #0d53bd, #06b6d4)', color: 'white', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', flex: 1 }}>Yes</button>
                            <button onClick={onClose} style={{ background: '#e2e4e6', color: '#666', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(20px, 4vw, 32px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', flex: 1 }}>No</button>
                        </>
                    ) : (
                        <button onClick={onClose} style={{ background: 'linear-gradient(135deg, #0d53bd, #06b6d4)', color: 'white', border: 'none', borderRadius: '30px', padding: 'clamp(10px, 2vw, 12px) clamp(24px, 5vw, 40px)', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: '600', cursor: 'pointer', width: '100%' }}>OK</button>
                    )}
                </div>
            </div>
            <style>{`@keyframes slideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
    );
};

function Navbar() {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [userName, setUserName] = useState('');
    const [userCredits, setUserCredits] = useState(0);
    const [showUserDropdown, setShowUserDropdown] = useState(false);

    const [showLogoutPopup, setShowLogoutPopup] = useState(false);

    // Check login status on mount and when token changes
    useEffect(() => {
        checkLoginStatus();

        // Listen for storage changes (for multi-tab support)
        window.addEventListener('storage', checkLoginStatus);
        window.addEventListener('authChange', checkLoginStatus);

        return () => {
            window.removeEventListener('storage', checkLoginStatus);
            window.removeEventListener('authChange', checkLoginStatus);
        };
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
                setShowUserDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showUserDropdown]);

    const checkLoginStatus = () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (token && user) {
            setIsLoggedIn(true);
            try {
                const userData = JSON.parse(user);
                setUserName(userData.fullName || userData.username || 'User');
                setUserCredits(userData.credits || 0);
            } catch (e) {
                setUserName('User');
                setUserCredits(0);
            }
        } else {
            setIsLoggedIn(false);
            setUserName('');
            setUserCredits(0);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsLoggedIn(false);
        setUserName('');
        setUserCredits(0);
        setShowUserDropdown(false);
        setShowLogoutPopup(false);
        navigate('/');
    };

    // ✅ Add this function to show logout confirmation
    const confirmLogout = () => {
        setShowUserDropdown(false); // Close dropdown first
        setShowLogoutPopup(true);
    };

    const cancelLogout = () => {
        setShowLogoutPopup(false);
    };

    const navbarStyles = `
        .navbar {
            padding: clamp(0.5rem, 2vw, 1rem) 0;
            background: #f8f9fcd6;
            backdrop-filter: blur(10px);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
            position: sticky;
            top: 0;
            z-index: 1000;
        }

        .navbar-brand {
            display: flex;
            align-items: center;
            margin-right: auto !important;
            margin-left: clamp(1rem, 3vw, 3rem) !important;
            text-decoration: none;
        }

        .navbar-brand img {
            height: clamp(28px, 4vw, 40px);
            width: auto;
        }
            
        .navbar-brand span {
            margin-left: clamp(5px, 1.5vw, 10px);
            font-size: clamp(1rem, 3vw, 1.5rem);
            font-weight: 900;
            background: linear-gradient(90deg, #125ed1ff, #06b6d4);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }

        .navbar-nav {
            display: flex;
            gap: clamp(1rem, 4vw, 4rem);
            margin-left: auto;
            margin-right: auto !important;
        }

        .nav-link {
            color: #0e4aa5ff !important;
            font-weight: 500;
            font-size: clamp(0.9rem, 2vw, 1rem);
            transition: all 0.3s ease;
            position: relative;
        }

        .nav-link:hover {
            color: #06b6d4 !important;
        }

        .nav-link::after {
            content: '';
            position: absolute;
            width: 0;
            height: 2px;
            bottom: -5px;
            left: 0;
            background: linear-gradient(90deg, #06b6d4, #a855f7);
            transition: width 0.3s ease;
        }

        .nav-link:hover::after {
            width: 100%;
        }

        .navbar-auth-space {
            display: flex;
            gap: 1rem;
            align-items: center;
            margin-right: clamp(0.5rem, 2vw, 1rem);
            min-width: auto;
        }

        .btn.login {
            background: transparent;
            color: #0e4aa5ff;
            border: 2px solid #0e4aa5ff;
            border-radius: 2rem;
            font-weight: 600;
            font-size: clamp(0.85rem, 2vw, 1rem);
            padding: clamp(6px, 1.5vw, 8px) clamp(12px, 2.5vw, 16px);
            transition: all 0.3s ease;
        }

        .btn.login:hover {
            background: #0e4aa5ff;
            color: white !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(14, 74, 165, 0.2);
        }

        .btn.sign {
            background: linear-gradient(135deg, #0e4aa5ff, #06b6d4);
            color: white;
            border: none;
            border-radius: 2rem;
            font-weight: 600;
            font-size: clamp(0.85rem, 2vw, 1rem);
            padding: clamp(6px, 1.5vw, 8px) clamp(12px, 2.5vw, 16px);
            transition: all 0.3s ease;
        }

        .btn.sign:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
            color: white !important;
        }

        /* User Dropdown Styles */
        .user-dropdown-container {
            position: relative;
        }

        .user-avatar {
            width: clamp(32px, 5vw, 40px);
            height: clamp(32px, 5vw, 40px);
            border-radius: 50%;
            background-color: grey;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: 700;
            font-size: clamp(1rem, 2.5vw, 1.2rem);
            cursor: pointer;
            transition: all 0.3s ease;
        }

        .user-dropdown-menu {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            background: white;
            border-radius: 12px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
            min-width: 220px;
            overflow: hidden;
            z-index: 1000;
            border: 1px solid rgba(6, 182, 212, 0.2);
        }

        .dropdown-header {
            padding: 12px 16px;
            border-bottom: 1px solid #eee;
            background: linear-gradient(135deg, #f8f9fc, #eef2ff);
        }

        .dropdown-header .user-name {
            font-weight: 700;
            color: #0e4aa5ff;
            font-size: 0.95rem;
        }

        .dropdown-header .user-email {
            font-size: 0.75rem;
            color: #666;
            margin-top: 2px;
        }

        /* Credits Display */
        .credits-display {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: linear-gradient(135deg, #f6f3ec, #f0edd9d0);
            border-bottom: 1px solid #f2e5ba;
        }

        .credits-icon {
            color: #d97706;
        }

        .credits-amount {
            font-weight: 700;
            color: #92400e;
            font-size: 1rem;
        }

        .credits-label {
            color: #92400e;
            font-size: 0.85rem;
            margin-left: auto;
        }

        .dropdown-item-custom {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 16px;
            color: #333;
            text-decoration: none;
            transition: all 0.2s ease;
            cursor: pointer;
            border: none;
            background: none;
            width: 100%;
            text-align: left;
            font-size: 0.9rem;
        }

        .dropdown-item-custom:hover {
            background: #f0f9ff;
            color: #0e4aa5ff;
        }

        .dropdown-item-custom.logout {
            color: #dc3545;
        }

        .dropdown-item-custom.logout:hover {
            background: #fee2e2;
            color: #dc3545;
        }

        .dropdown-divider {
            height: 1px;
            background: #eee;
            margin: 4px 0;
        }

        @media (max-width: 991px) {
            .navbar-brand {
                margin-left: auto !important;
            }
            .navbar-nav {
                gap: 0.5rem;
                flex-direction: column;
                margin-top: 1rem;
                text-align: center;
            }

            .nav-link {
                justify-content: center;
            }
            
            /* ✅ Remove hover effects on mobile */
            .nav-link:hover {
                color: #0e4aa5ff !important;
            }
            
            .nav-link::after {
                display: none !important;
            }
            
            .nav-link:hover::after {
                width: 0 !important;
            }

            .navbar-auth-space {
                flex-direction: column;
                width: 100%;
                margin-right: 0;
                margin-top: 1rem;
                padding-top: 1rem;
                border-top: 1px solid rgba(6, 182, 212, 0.2);
            }

            .navbar-auth-space .navbar-nav {
                flex-direction: column !important;
                width: 100%;
                gap: 0.75rem;
            }

            .navbar-auth-space .nav-item {
                width: 100%;
            }

            .navbar-auth-space .btn {
                width: 100%;
                display: block;
                text-align: center;
            }

            .user-dropdown-container {
                width: 100%;
            }
            
            .user-dropdown-menu {
                position: static;
                width: 100%;
                margin-top: 8px;
                box-shadow: none;
                border: 1px solid rgba(6, 182, 212, 0.2);
            }
            
            .user-avatar {
                margin: 0 auto;
            }
        }

        @media (max-width: 576px) {
            .navbar-brand span {
                font-size: clamp(0.9rem, 3.5vw, 1.1rem);
            }
            .navbar-brand img {
                height: clamp(24px, 5vw, 30px);
            }
        }
    `;

    const handleSmoothScroll = (e, targetId) => {
        e.preventDefault();

        // ✅ Safely close mobile navbar
        const navbarCollapse = document.getElementById('navbarNav');
        if (navbarCollapse && navbarCollapse.classList.contains('show')) {
            // Use Bootstrap's data attribute method instead of Collapse constructor
            const toggler = document.querySelector('.navbar-toggler');
            if (toggler) {
                toggler.click(); // ✅ Simulate click on hamburger to close
            }
        }

        // If not on home page, navigate to home first
        if (window.location.pathname !== '/') {
            navigate('/', { state: { scrollTo: targetId } });
            return;
        }

        // If on home page, scroll to element
        setTimeout(() => {
            const element = document.getElementById(targetId);
            if (element) {
                const navbarHeight = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        }, 100);
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Get user email from localStorage
    const getUserEmail = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user.email || '';
        } catch {
            return '';
        }
    };

    return (
        <>
            <style>{navbarStyles}</style>
            <nav className="navbar navbar-expand-lg">
                <a className="navbar-brand" href="/" style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                        src={logo}
                        alt="InsightDraft AI Logo"
                        style={{
                            objectFit: 'contain'
                        }}
                    />
                    <span>
                        InsightDraft AI
                    </span>
                </a>

                <button
                    className="navbar-toggler"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#navbarNav"
                    style={{ borderColor: '#06b6d4' }}
                >
                    <span
                        className="navbar-toggler-icon"
                        style={{ filter: 'invert(1)' }}
                    ></span>
                </button>

                <div className="collapse navbar-collapse" id="navbarNav">
                    <ul className="navbar-nav">
                        <li className="nav-item">
                            <a className="nav-link" href="/">
                                Home
                            </a>
                        </li>

                        <li className="nav-item">
                            <a
                                className="nav-link"
                                href="#how-to-use"
                                onClick={(e) => handleSmoothScroll(e, 'how-to-use')}
                            >
                                How to Use
                            </a>
                        </li>

                        <li className="nav-item">
                            <a
                                className="nav-link"
                                href="#about"
                                onClick={(e) => handleSmoothScroll(e, 'about')}
                            >
                                About
                            </a>
                        </li>

                        <li className="nav-item">
                            <a
                                className="nav-link"
                                href="#contact"
                                onClick={(e) => handleSmoothScroll(e, 'contact')}
                            >
                                Contact
                            </a>
                        </li>
                    </ul>

                    {/* Auth Buttons */}
                    <div className="navbar-auth-space">
                        {isLoggedIn ? (
                            <div className="user-dropdown-container">
                                <div
                                    className="user-avatar"
                                    title={userName}
                                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                                >
                                    {getInitials(userName)}
                                </div>

                                {showUserDropdown && (
                                    <div className="user-dropdown-menu">
                                        <div className="dropdown-header">
                                            <div className="user-name">{userName}</div>
                                            <div className="user-email">{getUserEmail()}</div>
                                        </div>

                                        {/* ✅ Credits Display */}
                                        <div className="credits-display">
                                            <Coins size={18} className="credits-icon" />
                                            <span className="credits-amount">{userCredits}</span>
                                            <span className="credits-label">Credits Remaining</span>
                                        </div>

                                        <a className="dropdown-item-custom" href="/my-analyses">
                                            <Save size={16} /> Saved Analyses
                                        </a>
                                        <div className="dropdown-divider"></div>
                                        <button
                                            className="dropdown-item-custom logout"
                                            onClick={confirmLogout}
                                        >
                                            <LogOut size={16} /> Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <ul className="navbar-nav d-flex flex-row gap-4 align-items-center">
                                <li className="nav-item">
                                    <a className="btn login" href="/login">
                                        Login
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className="btn sign" href="/signup">
                                        Sign Up
                                    </a>
                                </li>
                            </ul>
                        )}
                    </div>
                </div>
            </nav>

            {showLogoutPopup && (
                <CustomPopup
                    message="Are you sure you want to logout?"
                    type="warning"
                    onClose={cancelLogout}
                    onConfirm={handleLogout}
                    showConfirm={true}
                />
            )}
        </>
    );
}

export default Navbar;